// Step mapping — maps our transferStep to v56.2-style progress steps
import { isExplorableTxHash } from './format'

/**
 * #261 L4 — deBridge's own order states, in the user's words.
 *
 * The live ProgressPanel mount hands this builder `dexOrderStatus?.state` RAW (see the
 * `orderStatus:` prop passed by BridgePage's live-panel `useMemo`), and the rows below print their
 * sublabel verbatim, so "Created" — a deBridge protocol token, not English — was surfacing to the
 * user as their swap's status.
 *
 * Deliberately plain restatements, NOT reassurances: this builder cannot see whether a refund was
 * initiated, and a status line promising money movement it has not verified is the same class of
 * lie as the all-green failed swap that #261 F9 removed one screen over.
 *
 * `OrderCancelled` is the sharp one, and the obvious label for it is WRONG. In this system the
 * token does not mean "the user cancelled": `routes/dex.ts` synthesises it for 1Click `REFUNDED`
 * **or `FAILED` or `INCOMPLETE_DEPOSIT`**, for a same-chain `transfer_status === 'failed'`, and for
 * our poller's terminal `failed` verdict — its own comment there says the point is to "show a
 * failed (retryable) state". `useDexBridge`'s user-facing copy for exactly this case
 * (`DEX_TERMINAL_FAILURE_MESSAGE`) is hedged ON PURPOSE — "the order failed **or** was cancelled" —
 * because the system genuinely cannot tell which. A one-word 'Cancelled' resolves that hedge to the
 * reassuring side, and reads as a clean no-op to a user whose `INCOMPLETE_DEPOSIT` funds may not
 * have come back. So this label keeps the same hedge the rest of the codebase keeps.
 */
export const DLN_STATE_LABEL: Record<string, string> = {
  Created: 'Submitted',
  Fulfilled: 'Filled',
  SentUnlock: 'Finalising',
  ClaimedUnlock: 'Finalising',
  SentOrderCancel: 'Cancelling',
  OrderCancelled: 'Failed or cancelled',
  // Distinct from the above ON PURPOSE: this is the one state that says the cancel was actually
  // claimed on the source chain, i.e. the refund landed. Collapsing it into the hedge above would
  // erase the only signal the user has that their money came back.
  ClaimedOrderCancel: 'Refund claimed',
}

/**
 * #261 L4 — resolve an `orderStatus` to display copy, fail-CLOSED on anything unrecognised.
 *
 * This prop is overloaded by its two callers and the two cases must not be confused:
 *   · the LIVE panel passes a RAW deBridge state — must be mapped;
 *   · the DRAWER passes copy #261 F9 already humanised (`drawerOrderSublabel` in BridgePage,
 *     'Swap complete' / 'Swap failed') — must pass through untouched.
 *
 * Every raw state this system produces is a single PascalCase token; every caller-supplied display
 * string is a phrase. So an unmapped token that LOOKS raw is treated as a state added after this
 * map was written and degrades to the row's own generic copy. Widening this to "pass through
 * whatever we don't recognise" is the bug, not the simplification: that is exactly how "Created"
 * reached the user.
 *
 * The whitespace test is a heuristic and its limit is worth stating rather than implying: a future
 * multi-word raw state WOULD pass through. That is not reachable from any current producer (the
 * states are enumerated in `routes/dex.ts` and proxied deBridge tokens are PascalCase), and the
 * upstream schema is an unconstrained string, so this is a known edge and not a proof.
 *
 * An OWN-property check rather than a bare index: `orderStatus` arrives in an HTTP body, and a bare
 * lookup of `'__proto__'` or `'constructor'` returns a truthy NON-STRING off the prototype, which
 * would then be handed to React as a child. (`Object.prototype.hasOwnProperty.call` rather than
 * `Object.hasOwn` — the latter needs the ES2022 lib, above this project's target.)
 */
export function orderStatusLabel(orderStatus: string, fallback: string): string {
  if (Object.prototype.hasOwnProperty.call(DLN_STATE_LABEL, orderStatus)) {
    return DLN_STATE_LABEL[orderStatus]
  }
  return /\s/.test(orderStatus) ? orderStatus : fallback
}

/**
 * DEV-375 — the render vocabulary, with a THIRD terminal outcome.
 *
 * `'unknown'` means: this step's transaction was BROADCAST and we could not obtain a receipt for it.
 * It is not a softer 'error' and not a slower 'active' — it is the honest third answer, and it had
 * to become renderable before widening the error predicate could change anything the user sees.
 * Without it, a post-broadcast receipt read that failed still landed on `'error'`, under a red
 * "Transfer Failed" header, on three beta transfers that all DELIVERED.
 *
 * Every consumer that branches on this union is enumerated in
 * `.claude/review-log/DEV-375-receipt-unknown-not-failed-surface.md` (SPACE B).
 */
export type ProgressStepStatus = 'pending' | 'active' | 'done' | 'error' | 'unknown'

/**
 * DEV-375 — how a step that DID fail should read.
 *
 * Passed by the caller rather than sniffed from an error string here: keying on the message is the
 * defect #261 F9 removed (a legacy row carrying no `error` rendered three green checkmarks under
 * "Swap Failed"), and re-deriving it would reopen that. The caller knows, because it is the one
 * holding the classified failure.
 *
 * `'failed'` is the default at every call site that does not say otherwise, so an omission renders
 * exactly as it does today — the fail-closed direction. Only a caller that has POSITIVELY
 * established a receipt-unknown may soften a row (I2).
 */
export type StepFailureKind = 'failed' | 'unknown'

export interface ProgressStep {
  id: string
  label: string
  sublabel?: string
  status: ProgressStepStatus
  txHash?: string
  explorerUrl?: string
}

type TransferStep = 'idle' | 'switching_network' | 'approving' | 'signing' | 'burning' | 'confirming' | 'attesting' | 'minting' | 'complete' | 'error'

interface MakeStepsArgs {
  /**
   * #614 — a SAME-CHAIN deposit-flow order (Tron TRX↔USDT via 1Click). Passed EXPLICITLY by the
   * caller (it knows the provider); never inferred from `sourceChain === destChain`, so a same-chain
   * deBridge row reopened with the failure-step flag OFF keeps today's cross-chain copy byte for byte.
   * Relabels the hop step "Swapping on {chain}" — the "Delivering to" leg is the solver's fill.
   */
  sameChainDeposit?: boolean
  transferStep: TransferStep
  sourceChain: string | null
  destChain: string | null
  burnTxHash?: string | null
  mintTxHash?: string | null
  burnError?: string | null
  mintError?: string | null
  /**
   * DEV-375 — how a SOURCE-side failure reads: red 'error', or neutral 'unknown'.
   *
   * Scoped to the source side (the `burnError`-gated rows) ON PURPOSE. The receipt-unknown class is
   * a property of OUR post-broadcast read of the source transaction; a `mintError` is reported by
   * the backend, which either saw the mint fail or did not, so it is never "unknown" in this sense
   * and its row keeps 'error'.
   */
  failureKind?: StepFailureKind
  getExplorerUrl?: (hash: string, chain: any) => string
}

function stepStatus(
  current: TransferStep,
  myPhases: TransferStep[],
  errorPhase?: boolean,
  failureKind: StepFailureKind = 'failed',
): ProgressStepStatus {
  // DEV-375. Defaulting to 'failed' is what keeps every unconverted call site byte-identical.
  if (errorPhase) return failureKind === 'unknown' ? 'unknown' : 'error'

  // 'approving' sits between the network switch and the wallet signature: it is a REAL on-chain
  // ERC-20 approve, emitted only by the contract-account bridge() path (#137 Phase 1). Inserting it
  // here cannot move any EOA phase relative to another — they keep their existing order — so the EOA
  // rendering is unchanged for every step it can actually reach.
  const ORDER: TransferStep[] = [
    'idle', 'switching_network', 'approving', 'signing', 'burning',
    'confirming', 'attesting', 'minting', 'complete',
  ]
  const currentIdx = ORDER.indexOf(current)
  const firstIdx = Math.min(...myPhases.map(p => ORDER.indexOf(p)))
  const lastIdx = Math.max(...myPhases.map(p => ORDER.indexOf(p)))

  if (currentIdx > lastIdx) return 'done'
  if (currentIdx >= firstIdx && currentIdx <= lastIdx) return 'active'
  return 'pending'
}

export function makeSteps({
  transferStep,
  sourceChain,
  destChain,
  burnTxHash,
  mintTxHash,
  burnError,
  mintError,
  failureKind = 'failed',
  getExplorerUrl,
  sameChainDeposit = false,
}: MakeStepsArgs): ProgressStep[] {
  const srcName = sourceChain ? sourceChain.charAt(0).toUpperCase() + sourceChain.slice(1) : 'Source'
  const dstName = destChain ? destChain.charAt(0).toUpperCase() + destChain.slice(1) : 'Destination'

  return [
    {
      id: 'approve',
      label: 'Approving',
      // Contract accounts (Base Account / any ERC-4337 wallet) bridge via `bridge()`, which pulls with
      // transferFrom and therefore needs a real ERC-20 approval transaction first. This row is that
      // step: it must read as an explicit action the user has to confirm in their wallet, NOT as a
      // silent pause while a second popup they weren't told about waits for them.
      sublabel: transferStep === 'approving'
        ? 'Confirm the USDC approval in your wallet'
        : 'Giving permission to move your funds',
      status: stepStatus(transferStep, ['switching_network', 'approving'], !!burnError && (transferStep === 'switching_network' || transferStep === 'approving'), failureKind),
    },
    {
      id: 'sign',
      label: 'Confirm in wallet',
      sublabel: 'Open your wallet and approve',
      status: stepStatus(transferStep, ['signing'], !!burnError && transferStep === 'signing', failureKind),
    },
    {
      id: 'source',
      label: `Confirming on ${srcName}`,
      sublabel: burnTxHash ? `TX: ${burnTxHash.slice(0, 10)}...` : 'Waiting for the network to process',
      status: stepStatus(transferStep, ['burning', 'confirming'], !!burnError && ['burning', 'confirming'].includes(transferStep as string), failureKind),
      txHash: burnTxHash ?? undefined,
      explorerUrl: burnTxHash && getExplorerUrl ? getExplorerUrl(burnTxHash, sourceChain) : undefined,
    },
    {
      id: 'bridge',
      // #614 — a SAME-CHAIN deposit-flow order (Tron TRX↔USDT via 1Click) renders this cross-chain
      // template too (the compact makeDexSteps card describes ONE atomic tx, which it is not). The
      // hop copy would be false on one chain, so name the swap instead; the "Delivering to" leg below
      // is the solver's fill and stays.
      label: sameChainDeposit ? `Swapping on ${srcName}` : 'Moving between chains',
      // #336 D1 — token-agnostic ON PURPOSE. Cross-chain DEX swaps reach these rows too
      // (BridgePage.tsx:239 picks makeSteps whenever the route is NOT same-chain), so an ETH→ETH
      // swap read "for USDC transfers". Do not re-add a token name here.
      sublabel: 'Usually 1–5 min',
      // DEV-375 finding #1 — a DOWNSTREAM failure (deBridge OrderCancelled, 1Click
      // REFUNDED/FAILED/INCOMPLETE_DEPOSIT) routes `transferStep` here (`crossChainStep` maps it to
      // 'attesting', not 'confirming', because the SOURCE leg succeeded — see BridgePage.tsx). Before
      // this gate this row could never render 'error' at all, so a caller-confirmed downstream
      // failure left this row a permanently 'active' spinner under a red panel title. Gated on
      // `burnError` the same way the 'source'/'sign'/'approve' rows are, restricted to the ONE phase
      // this row owns.
      status: stepStatus(transferStep, ['attesting'], !!burnError && transferStep === 'attesting', failureKind),
    },
    {
      id: 'destination',
      label: `Delivering to ${dstName}`,
      // A MINTED_EXTERNALLY sentinel = delivered, but no linkable OUR-side tx — show a clean "Delivered"
      // instead of "TX: MINTED_EX…" + a broken explorer link.
      sublabel: isExplorableTxHash(mintTxHash) ? `TX: ${mintTxHash.slice(0, 10)}...`
        : mintTxHash ? 'Delivered to your wallet'
        : 'Almost there — finalizing on destination',
      status: stepStatus(transferStep, ['minting'], !!mintError && transferStep === 'minting'),
      txHash: isExplorableTxHash(mintTxHash) ? mintTxHash : undefined,
      explorerUrl: isExplorableTxHash(mintTxHash) && getExplorerUrl ? getExplorerUrl(mintTxHash, destChain) : undefined,
    },
  ]
}

export function makeDexSteps({
  dexStep,
  sourceChain,
  destChain,
  sourceTxHash,
  orderStatus,
  failedStep,
  failureKind = 'failed',
  getExplorerUrl,
}: {
  dexStep: string
  sourceChain: string | null
  destChain: string | null
  sourceTxHash?: string | null
  orderStatus?: string | null
  /**
   * The failure message, if the caller has one. ACCEPTED BUT NO LONGER READ: until #261 F9's fixer
   * pass the fallback below was keyed on this string being truthy, which meant an errored row that
   * carried no message — a legacy stored order, or any row from `/bridge/history`, whose payload has
   * no such field — rendered as three green checkmarks under the title "Swap Failed". The fallback is
   * keyed on the PHASE now. The property stays in this type because every call site still passes it
   * and removing it is an exported-signature change; it is deliberately not destructured so nothing
   * can quietly start depending on it again.
   */
  error?: string | null
  /**
   * #261 F9 — the phase the swap was in when it failed, when the caller knows it (the live hook
   * state, or `StoredDexOrder.failedStep` for a row reopened from Activity). Optional ON PURPOSE:
   * this builder holds no feature flag, so a flag-off call site simply passes nothing and gets the
   * pre-F9 rendering byte-for-byte. Absent or unrecognised selects the legacy short-circuit below.
   */
  failedStep?: string | null
  /**
   * DEV-375 — how the failing row reads: red 'error', or neutral 'unknown'.
   *
   * Optional and defaulting to 'failed' for the same reason `failedStep` is optional: a caller that
   * cannot classify the failure gets today's rendering byte-for-byte. It is NOT gated on
   * `VITE_FAILURE_STEP_STATE` at the call site — a receipt-unknown must never render as a red
   * "Failed" on either side of that flag (P-B), which is the whole point of the ticket.
   */
  failureKind?: StepFailureKind
  getExplorerUrl?: (hash: string, chain: any) => string
}): ProgressStep[] {
  const srcName = sourceChain ? sourceChain.charAt(0).toUpperCase() + sourceChain.slice(1) : 'Source'
  const dstName = destChain ? destChain.charAt(0).toUpperCase() + destChain.slice(1) : 'Destination'
  const isSameChain = sourceChain === destChain

  const DEX_ORDER = ['idle', 'fetching_quote', 'confirming_route', 'approving', 'signing', 'broadcasting', 'polling', 'complete', 'error']
  const dexIdx = DEX_ORDER.indexOf(dexStep)
  // #261 F9. The per-step path is selected by ROW COVERAGE, not by DEX_ORDER membership. Those are
  // NOT the same set: DEX_ORDER has nine members and the rows below span only these six. The other
  // three ('idle', 'complete', 'error') sit outside every row's phase span, so routing them into the
  // per-step path renders a FAILED swap as all-'pending' ('idle', an invisible failure) or as three
  // green checkmarks ('complete'/'error', whose indices exceed every lastIdx) — the exact hazard I-5
  // exists to forbid. Anything outside this set is therefore treated as "the caller did not tell us
  // which phase failed" and selects the retained short-circuit inside dexStatus.
  const ROW_PHASES = ['fetching_quote', 'confirming_route', 'approving', 'signing', 'broadcasting', 'polling']
  const failedIdx = failedStep != null && ROW_PHASES.includes(failedStep) ? DEX_ORDER.indexOf(failedStep) : -1

  // DEV-375 — the ONE place this builder turns "this row failed" into a status. Both error returns
  // below go through it, so a receipt-unknown cannot soften one branch and leave the other red.
  const failStatus = (): ProgressStepStatus => (failureKind === 'unknown' ? 'unknown' : 'error')

  function dexStatus(myPhases: string[]): ProgressStepStatus {
    const firstIdx = Math.min(...myPhases.map(p => DEX_ORDER.indexOf(p)))
    const lastIdx = Math.max(...myPhases.map(p => DEX_ORDER.indexOf(p)))

    // ── #261 F9 PER-STEP PATH ────────────────────────────────────────────────────────────────────
    // Only the row whose phases contain the failing one reads as failed; rows entirely before it
    // completed, rows after it were never reached. `failedIdx !== -1` means the caller named one of
    // the SIX row-bearing phases (see ROW_PHASES above), and those six are partitioned across the
    // three rows — quote {fetching_quote, confirming_route}, sign {approving, signing, broadcasting},
    // confirm {polling} — so exactly one row can match. I-8. (The claim is about ROW_PHASES, not
    // about DEX_ORDER, whose other three members cover no row at all and never reach here.)
    //
    // GATE-4 F-1: `dexStep === 'error'` is REQUIRED here, not redundant with `failedIdx !== -1`.
    // The two inputs come from different places and can disagree: `dexStep` is derived from the row's
    // CURRENT status, while `failedStep` is a field that persists in localStorage. `updateDexOrder`
    // merges partials (`{ ...order, ...updates }`, dexOrderStorage.ts:140) and NO complete-writer
    // clears `failedStep` — so a transient-404/cancelled reconcile write racing an in-flight
    // 'fulfilled' poll leaves a stored row holding BOTH `orderStatus:'complete'` and
    // `failedStep:'polling'`. Without this conjunct that row reopens from Activity as a red confirm
    // row underneath the title "Swap Complete": a step marked failed on a swap that succeeded. That
    // is the inverse of the all-green bug below and the same lie about the user's money, so the gate
    // gets the same treatment — the per-step path engages ONLY for a terminally-errored order.
    if (failedIdx !== -1 && dexStep === 'error') {
      if (myPhases.includes(failedStep as string)) return failStatus()
      return failedIdx > lastIdx ? 'done' : 'pending'
    }

    // ── THE RETAINED SHORT-CIRCUIT — DO NOT DELETE (I-5 / falsifier F-A) ─────────────────────────
    // Pre-F9 this was the ONLY error handling here, and it stays as the absent-`failedStep` branch:
    // a caller that cannot say which phase failed degrades to the old all-red, which is loud and
    // honest. Deleting it is not a simplification — `DEX_ORDER.indexOf('error') === 8` sits ABOVE
    // every row's lastIdx (quote 2, sign 5, confirm 6), so the comparison below would return 'done'
    // for all three rows and a FAILED swap would render as three green checkmarks.
    //
    // It is keyed on the PHASE, never on the `error` STRING. That distinction is the whole guard: an
    // errored row with no message is not a healthy row. Two live callers hand this builder exactly
    // that — a legacy `storedDexOrders` row written before F9 (no `error`, no `failedStep`), and any
    // row from `/bridge/history`, whose payload carries neither field and therefore never can. Keyed
    // on `error` those rendered THREE GREEN CHECKMARKS under the title "Swap Failed"; keyed on the
    // phase they render the honest all-red.
    if (dexStep === 'error' && failedIdx === -1) return failStatus()

    if (dexIdx > lastIdx) return 'done'
    if (dexIdx >= firstIdx && dexIdx <= lastIdx) return 'active'
    return 'pending'
  }

  const steps: ProgressStep[] = [
    {
      id: 'quote',
      label: 'Fetching Quote',
      // Provider-agnostic ALWAYS — we must never surface the underlying aggregator (deBridge / Jupiter /
      // 1inch / near_1click) to the user. The old `via ${provider}` sublabel leaked the raw route name
      // beneath the loading state on same-chain swaps; this mirrors FeeSection's provider-neutral copy.
      sublabel: 'Finding the best route…',
      status: dexStatus(['fetching_quote', 'confirming_route']),
    },
    {
      id: 'sign',
      label: 'Sign & Broadcast',
      sublabel: dexStep === 'approving' ? 'Approving token spend' : (sourceTxHash ? `TX: ${sourceTxHash.slice(0, 10)}...` : 'Wallet authorization'),
      status: dexStatus(['approving', 'signing', 'broadcasting']),
      txHash: sourceTxHash ?? undefined,
      explorerUrl: sourceTxHash && getExplorerUrl ? getExplorerUrl(sourceTxHash, sourceChain) : undefined,
    },
  ]

  // UNREACHABLE FROM ANY LIVE ENTRY POINT, stated so nobody re-derives it (CLAUDE.md): every call
  // reaches this builder through BridgePage's `buildRouteSteps`, which routes `sameChain ?
  // makeDexSteps : makeSteps` — so `isSameChain` is always true here and the branch below never
  // renders today. It is kept, and covered, because it is the builder's own contract rather than a
  // caller's: the day a cross-chain route is pointed at this builder, the raw-enum guarantee must
  // already hold. The LIVE half of #261 L4 is the `else` branch — the confirm row.
  if (!isSameChain) {
    steps.push({
      id: 'bridge',
      label: `${srcName} → ${dstName}`,
      sublabel: orderStatus ? orderStatusLabel(orderStatus, 'Tracking order') : 'Tracking order',
      status: dexStatus(['polling']),
    })
  } else {
    steps.push({
      id: 'confirm',
      label: `${srcName} Confirmation`,
      sublabel: orderStatus ? orderStatusLabel(orderStatus, 'Confirming swap') : 'Confirming swap',
      status: dexStatus(['polling']),
    })
  }

  return steps
}
